import React from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'warning' | 'info';
    isLoading?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
    isLoading = false,
}) => {
    if (!isOpen) return null;

    const variantConfig = {
        danger: {
            icon: ExclamationTriangleIcon,
            iconBg: 'bg-red-500/10 border border-red-500/30',
            iconColor: 'text-red-500',
            buttonClass: 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-500/20',
        },
        warning: {
            icon: ExclamationTriangleIcon,
            iconBg: 'bg-orange-500/10 border border-orange-500/30',
            iconColor: 'text-orange-500',
            buttonClass: 'bg-orange-500 hover:bg-orange-400 text-white shadow-lg shadow-orange-500/20',
        },
        info: {
            icon: ExclamationTriangleIcon, // Could use an Info icon instead
            iconBg: 'bg-info/10 border border-info/30',
            iconColor: 'text-info',
            buttonClass: 'bg-info hover:bg-blue-500 text-white shadow-lg shadow-info/20',
        },
    };

    const { icon: Icon, iconBg, iconColor, buttonClass } = variantConfig[variant];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
                onClick={onClose}
            />

            {/* Modal Container */}
                        <div className="relative glass-panel rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.6)] border border-white/10 max-w-2xl w-full overflow-hidden animate-in zoom-in-95 fade-in duration-200">
                {/* Visual Accent */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-${variant === 'danger' ? 'red' : variant === 'warning' ? 'orange' : 'blue'}-500 to-transparent`}></div>

                {/* Header/Content Padding */}
                <div className="p-6">
                    <div className="flex items-start gap-4">
                        <div className={`p-2.5 rounded-xl ${iconBg} ${iconColor} flex-shrink-0 relative overflow-hidden`}>
                            <div className="absolute inset-0 bg-white/5 opacity-0 hover:opacity-100 transition-opacity"></div>
                            <Icon className="h-6 w-6 relative z-10" />
                        </div>

                        <div className="flex-1 pt-1">
                            <div className="flex justify-between items-start">
                                <h3 className="text-xl font-black text-white leading-tight tracking-tight">
                                    {title}
                                </h3>
                                <button
                                    onClick={onClose}
                                    className="text-text-secondary hover:text-white transition-colors -mt-1 -mr-1 p-1 hover:bg-white/10 rounded-lg"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>
                            <p className="mt-3 text-sm text-text-secondary leading-relaxed font-medium">
                                {message}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="bg-black/40 px-6 py-5 flex flex-col-reverse sm:flex-row justify-end gap-3 border-t border-white/5 backdrop-blur-xl">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isLoading}
                                                 className="w-full sm:w-auto flex-1 min-w-0 rounded-xl border-white/10 bg-white/5 hover:bg-white/10 px-5 transition-all text-white font-black tracking-normal text-[10px] uppercase h-10 whitespace-normal break-words"
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        onClick={onConfirm}
                        loading={isLoading}
                                                 className={`w-full sm:w-auto flex-1 min-w-0 rounded-xl px-6 font-black tracking-normal text-[10px] uppercase h-10 transition-all active:scale-95 border-none ${buttonClass} whitespace-normal break-words`}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};
